import { Button, Col, Form, Row, Space, theme } from "antd";
import { useEffect, useState } from "react";
import Data from "../controls/Data";
import { SearchQuery } from "../code/search-query/search-query";
import {
  companyPoliciesViewColumns,
  mapCompanyPoliciesViewQuery,
} from "./PoliciesView.default";
import { getCompanies, getCompanyPolicies } from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import { DataRequest } from "../controls/Data.default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { createGridRequest } from "../hooks/useGridRequest";
import { createSearchRequest } from "../hooks/useSearchRequest";

export interface PoliciesViewProps {
  readonly trigger?: string;
  readonly onAction?: (key: string, record: any) => void;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
}

function PoliciesView(props: PoliciesViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
  }>({
    searchQuery: {
      conditions: [],
    },
  });
  const [valuesValid, setValuesValid] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  useEffect(() => {
    setContext({ ...context });
  }, [props.trigger]);

  // Events

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const searchQuery = mapCompanyPoliciesViewQuery(values);
    setContext({
      searchQuery,
    });
    props.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({
      searchQuery: {
        conditions: [],
      },
    });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  // Methods

  const baseRequest = createGridRequest(getCompanyPolicies, { retry: true });

  // Derive a non-null "scope" cell: the Data grid skips a column transform when the value is null, so a
  // platform-wide policy (no company) would render blank — surface "Platform" here instead.
  const request = (req: DataRequest) =>
    baseRequest(req).then((result) => ({
      ...result,
      data: result.data.map((row: any) => ({
        ...row,
        scope: row.companyName ?? "Platform",
      })),
    }));

  const companiesRequest = createSearchRequest(getCompanies, "name");

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={!valuesValid || loading} onClick={onClearData}>
            Clear
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!valuesValid}
            onClick={onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          className="w-72 overflow-y-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                  },
                  {
                    type: "textbox",
                    name: "code",
                    label: "Code",
                  },
                  {
                    type: "combobox",
                    name: "company",
                    label: "Company",
                    request: companiesRequest,
                  },
                ],
              },
            ],
          }}
        />
      </Col>
      {/* Data */}
      <Data
        columns={companyPoliciesViewColumns}
        context={context}
        pageSize={100}
        actions={[
          {
            key: "preview",
            label: "Preview",
            handler: onAction,
          },
          {
            key: "update",
            label: "Edit",
            disabled: !permissions.can("CompanyPolicies", "edit"),
            handler: onAction,
          },
          {
            key: "delete",
            label: "Delete",
            danger: true,
            disabled: !permissions.can("CompanyPolicies", "delete"),
            handler: onAction,
          },
        ]}
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
      />
    </Row>
  );
}

export default PoliciesView;
